/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
/* eslint-disable function-call-argument-newline */
import dayjs from 'dayjs';
import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  MenuItem,
  OutlinedInput,
  Select as MuiSelect,
} from '@mui/material';
import { Calendar } from 'primereact/calendar';

import UseAxios from '../../../Api/http/http';
import dropdwn from '../../../Assets/images/svg/dar_dropdown_arrow.svg';
import {
  getMilestoneDropdownOptions,
  getMilestonePoNumbers,
  searchPoNumbers,
  getZohoInvoices,
  getProjectInvoices,
  createInvoice,
  updateInvoice,
  getSingleInvoice,
} from '../../../Api/request/milestone';

/* -------------------------------------------------------------------------- */
/*  Date helpers                                                               */
/* -------------------------------------------------------------------------- */
const MONTH_ABBR = {
  Jan: '01',
  Feb: '02',
  Mar: '03',
  Apr: '04',
  May: '05',
  Jun: '06',
  Jul: '07',
  Aug: '08',
  Sep: '09',
  Oct: '10',
  Nov: '11',
  Dec: '12',
};

const parseDateForInput = (dateVal) => {
  if (!dateVal || dateVal === '-' || dateVal === '9999-01-01' || dateVal === '0000-00-00') return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateVal)) return dateVal;
  const match = /^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/.exec(dateVal);
  if (match) return `${match[3]}-${MONTH_ABBR[match[2]] || '01'}-${match[1].padStart(2, '0')}`;
  return '';
};

const normalizeText = (value) => String(value || '').trim()
  .toLowerCase();

const resolveMilestoneTypeIdFromRow = (milestoneRow, milestoneTypeOptions = []) => {
  const directId = milestoneRow?.milestoneTypeId ?? milestoneRow?.milestone_type_id;
  if (directId !== undefined && directId !== null && String(directId).trim() !== '') {
    return String(directId);
  }
  const rowTypeLabel = (
    milestoneRow?.milestoneType
    ?? milestoneRow?.milestone_type_name
    ?? milestoneRow?.milestone_type
    ?? ''
  );
  if (!normalizeText(rowTypeLabel)) return '';
  const matched = milestoneTypeOptions.find((item) => (
    normalizeText(item?.label) === normalizeText(rowTypeLabel)
    || normalizeText(item?.value) === normalizeText(rowTypeLabel)
  ));
  return matched?.id != null ? String(matched.id) : '';
};

const resolveZohoClientName = (source = {}) => (
  source?.milestone_zoho_customer_name
  || source?.zoho_customer_name
  || source?.zohoCustomerName
  || source?.zohoClientName
  || source?.client_name
  || ''
);

/* -------------------------------------------------------------------------- */
/*  Constants                                                                  */
/* -------------------------------------------------------------------------- */
const EMPTY_INVOICE = {
  invoiceId: '',
  milestoneId: '',
  poId: '',
  poText: '',
  milestoneStatusId: '',
  milestoneTypeId: '',
  invoiceStatusId: '',
  milestone: '',
  category: '',
  contValueP: '',
  amount: '',
  cogs: '',
  ga: '',
  tp: '',
  dueDate: '',
  replannedDueDate: '',
  milestoneRemarks: '',
  zohoInvoiceId: '',
  invoiceAmount: '',
  invoiceCogs: '',
  invoiceGa: '',
  invoiceTp: '',
  invoiceAmountWithTax: '',
  invoiceDate: '',
  invoiceTypeId: '',
  creditNoteLinkedId: '',
  revenueDate: '',
  remarks: '',
  billedEffort: '',
  paymentStatusId: '',
  collectionCreditPeriod: '',
};

/* -------------------------------------------------------------------------- */
/*  InvoiceFormDialog                                                          */
/* -------------------------------------------------------------------------- */
const InvoiceFormDialog = ({
  visible,
  onHide,
  mode,
  milestoneRow,
  rowData,
  projectId,
  token,
  onSuccess,
}) => {
  const [form, setForm] = useState(EMPTY_INVOICE);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [msStatuses, setMsStatuses] = useState([]);
  const [msTypes, setMsTypes] = useState([]);
  const [invStatuses, setInvStatuses] = useState([]);
  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [paymentStatuses, setPaymentStatuses] = useState([]);
  const [zohoInvoices, setZohoInvoices] = useState([]);
  const [projectInvoices, setProjectInvoices] = useState([]);
  const [zohoClientName, setZohoClientName] = useState('');
  const [showTaxPopover, setShowTaxPopover] = useState(false);
  const [taxPercent, setTaxPercent] = useState('');

  const [showPoSuggestions, setShowPoSuggestions] = useState(false);
  const [filteredPoNumbers, setFilteredPoNumbers] = useState([]);

  const [zohoText, setZohoText] = useState('');
  const [filteredZohoInvoices, setFilteredZohoInvoices] = useState([]);
  const [showZohoSuggestions, setShowZohoSuggestions] = useState(false);

  const taxWrapRef = useRef(null);
  const poWrapRef = useRef(null);
  const zohoWrapRef = useRef(null);
  const poSearchTimerRef = useRef(null);

  useEffect(() => {
    if (!visible) return;
    setError('');
    setShowTaxPopover(false);
    setTaxPercent('');
    setZohoText('');
    setZohoClientName('');
    setFilteredZohoInvoices([]);
    setShowZohoSuggestions(false);

    // rowData from the flattened table uses camelCase (invoiceId); support both
    const editInvoiceId = rowData?.invoiceId ?? rowData?.invoice_id;
    const isEdit = mode === 'edit' && editInvoiceId;

    if (isEdit) setLoading(true);

    Promise.all([
      UseAxios(getMilestoneDropdownOptions('milestone-statuses')),
      UseAxios(getMilestoneDropdownOptions('milestone-types')),
      UseAxios(getMilestoneDropdownOptions('invoice-statuses')),
      UseAxios(getMilestoneDropdownOptions('invoice-types')),
      UseAxios(getMilestoneDropdownOptions('payment-statuses')),
      UseAxios(getZohoInvoices(projectId)),
      UseAxios(getProjectInvoices(projectId)),
      UseAxios(getMilestonePoNumbers(projectId)),
      isEdit ? UseAxios(getSingleInvoice(editInvoiceId)) : Promise.resolve(null),
    ]).then(([msRes, mtRes, isRes, itRes, psRes, ziRes, projInvRes, poRes, invoiceRes]) => {
      setMsStatuses(msRes?.data?.data || []);
      setMsTypes(mtRes?.data?.data || []);
      setInvStatuses(isRes?.data?.data || []);
      setInvoiceTypes(itRes?.data?.data || []);

      const fetchedPaymentStatuses = psRes?.data?.data || [];
      setPaymentStatuses(fetchedPaymentStatuses);

      setZohoInvoices(ziRes?.data?.data || []);
      setProjectInvoices(projInvRes?.data?.data || []);
      const projectPoList = poRes?.data?.data || [];

      const pendingCollectionStatus = fetchedPaymentStatuses.find(
        (status) => (status.label || status.name || status.value || '').toLowerCase().trim() === 'pending collection',
      );
      const defaultPaymentStatusId = pendingCollectionStatus ? String(pendingCollectionStatus.id) : '';

      if (isEdit && invoiceRes) {
        const data = invoiceRes?.data?.data || {};
        setForm({
          invoiceId: String(data.invoice_id || ''),
          milestoneId: String(data.mile_id || ''),
          poId: data.po_id ? String(data.po_id) : '',
          poText: data.po_number || '',
          milestoneStatusId: data.milestone_status_id ? String(data.milestone_status_id) : '',
          milestoneTypeId: data.milestone_type_id ? String(data.milestone_type_id) : '',
          invoiceStatusId: data.invoice_status_id ? String(data.invoice_status_id) : '',
          milestone: data.milestone || '',
          category: data.category || '',
          contValueP: data.cont_value_p ?? '',
          amount: data.amount ?? '',
          cogs: data.cogs ?? '',
          ga: data.ga ?? '',
          tp: data.tp ?? '',
          dueDate: parseDateForInput(data.due_date),
          replannedDueDate: parseDateForInput(data.replanned_due_date),
          milestoneRemarks: data.m_remarks || '',
          zohoInvoiceId: data.zoho_invoice_id ? String(data.zoho_invoice_id) : '',
          invoiceAmount: data.invoice_amount ?? '',
          invoiceCogs: data.invoice_cogs ?? '',
          invoiceGa: data.invoice_ga ?? '',
          invoiceTp: data.invoice_tp ?? '',
          invoiceAmountWithTax: data.invoice_amount_with_tax ?? '',
          invoiceDate: parseDateForInput(data.invoice_date),
          invoiceTypeId: data.invoice_type_id ? String(data.invoice_type_id) : '',
          creditNoteLinkedId: data.credit_note_linked_id ? String(data.credit_note_linked_id) : '',
          revenueDate: parseDateForInput(data.revenue_date),
          remarks: data.remarks || '',
          billedEffort: data.billed_effort ?? '',
          paymentStatusId: data.payment_status_id ? String(data.payment_status_id) : '',
          collectionCreditPeriod: data.collection_credit_period ?? '',
        });

        const zohoId = data.zoho_invoice_id ? String(data.zoho_invoice_id) : '';
        const zohoMatch = (ziRes?.data?.data || []).find((zohoInv) => String(zohoInv.zoho_invoice_id) === zohoId);
        setZohoText(zohoMatch?.zoho_invoice_number || '');
        setZohoClientName(resolveZohoClientName(data));
        setLoading(false);
      } else if (mode === 'add' && milestoneRow) {
        const validPoNumber = milestoneRow.poNumber && milestoneRow.poNumber !== '-'
          ? milestoneRow.poNumber : '';
        const resolvedMilestoneTypeId = resolveMilestoneTypeIdFromRow(
          milestoneRow,
          mtRes?.data?.data || [],
        );
        const matchedPo = projectPoList.find((po) => (
          validPoNumber
          && (po.po_number === validPoNumber || po.po_number?.toLowerCase() === validPoNumber.toLowerCase())
        ) || (milestoneRow.poId && String(po.po_id) === String(milestoneRow.poId)));
        const initialCreditPeriod = matchedPo && matchedPo.credit_period != null
          ? String(matchedPo.credit_period)
          : '';
        setForm({
          ...EMPTY_INVOICE,
          milestoneId: String(milestoneRow.milestoneId ?? milestoneRow.milestone_id ?? ''),
          milestone: milestoneRow.milestone || '',
          category: milestoneRow.category || '',
          contValueP: milestoneRow.rawContValueP != null ? String(milestoneRow.rawContValueP) : '',
          amount: milestoneRow.rawAmount != null ? String(milestoneRow.rawAmount) : '',
          cogs: milestoneRow.rawCogs != null ? String(milestoneRow.rawCogs) : '',
          ga: milestoneRow.rawGa != null ? String(milestoneRow.rawGa) : '',
          tp: milestoneRow.rawTp != null ? String(milestoneRow.rawTp) : '',
          dueDate: parseDateForInput(milestoneRow.rawDueDate ?? ''),
          replannedDueDate: parseDateForInput(milestoneRow.rawReplannedDueDate ?? ''),
          milestoneRemarks: milestoneRow.remarks || '',
          milestoneStatusId: milestoneRow.milestoneStatusId ? String(milestoneRow.milestoneStatusId) : '',
          milestoneTypeId: resolvedMilestoneTypeId,
          invoiceStatusId: milestoneRow.invoiceStatusId ? String(milestoneRow.invoiceStatusId) : '',
          poId: validPoNumber ? (milestoneRow.poId != null ? String(milestoneRow.poId) : '') : '',
          poText: validPoNumber,
          paymentStatusId: defaultPaymentStatusId,
          collectionCreditPeriod: initialCreditPeriod,
        });
        setZohoClientName(resolveZohoClientName(milestoneRow));
      } else {
        setForm({
          ...EMPTY_INVOICE,
          paymentStatusId: defaultPaymentStatusId,
        });
      }
    });
  }, [visible, mode, projectId, milestoneRow, rowData]);

  if (!visible) return null;

  const setField = (key, val) => {
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  const renderSelectValue = (selectedValue, options, labelKey = 'label', valueKey = 'id') => {
    if (!selectedValue) {
      return <span className="ms-dialog__placeholder">Select</span>;
    }
    const match = options.find((item) => String(item[valueKey]) === String(selectedValue));
    return match ? match[labelKey] : selectedValue;
  };

  const applyTax = () => {
    const pct = parseFloat(taxPercent);
    const base = parseFloat(form.invoiceAmount);
    if (Number.isFinite(pct) && Number.isFinite(base)) {
      const withTax = base + (pct / 100) * base;
      setField('invoiceAmountWithTax', String(withTax.toFixed(2)));
    }
    setShowTaxPopover(false);
    setTaxPercent('');
  };

  const handleSubmit = async () => {
    if (!form.invoiceAmount) {
      setError('Invoice Amount is required.');
      return;
    }
    if (mode === 'add' && !form.milestoneTypeId) {
      setError('Milestone Type is required.');
      return;
    }
    if (isCreditNote && !form.creditNoteLinkedId) {
      setError('Invoice Id Related to Credit Note is required for Credit Note type.');
      return;
    }

    if (!form.collectionCreditPeriod && form.collectionCreditPeriod !== 0 && form.collectionCreditPeriod !== '0') {
      setError('Collection Credit Period is required.');
      return;
    }

    setSubmitting(true);
    setError('');

    const milestoneSnapshot = mode === 'add' ? {
      milestone: form.milestone,
      category: form.category,
      contValueP: form.contValueP,
      amount: form.amount,
      cogs: form.cogs,
      ga: form.ga,
      tp: form.tp,
      dueDate: form.dueDate,
      replannedDueDate: form.replannedDueDate,
      milestoneStatusId: form.milestoneStatusId,
      milestoneTypeId: form.milestoneTypeId,
      invoiceStatusId: form.invoiceStatusId,
      poId: form.poId,
    } : undefined;

    const payload = {
      invoiceAmount: form.invoiceAmount,
      invoiceCogs: form.invoiceCogs,
      invoiceGa: form.invoiceGa,
      invoiceTp: form.invoiceTp,
      invoiceAmountWithTax: form.invoiceAmountWithTax,
      invoiceDate: form.invoiceDate,
      invoiceTypeId: form.invoiceTypeId,
      creditNoteLinkedId: form.creditNoteLinkedId,
      revenueDate: form.revenueDate,
      remarks: form.remarks,
      billedEffort: form.billedEffort,
      paymentStatusId: form.paymentStatusId,
      collectionCreditPeriod: form.collectionCreditPeriod,
      zohoInvoiceId: form.zohoInvoiceId,
      ...(milestoneSnapshot ? { milestoneSnapshot } : {}),
    };

    const req = mode === 'edit'
      ? updateInvoice(rowData.invoiceId ?? rowData.invoice_id, { ...payload, milestoneId: form.milestoneId })
      : createInvoice(form.milestoneId, payload);

    const result = await UseAxios({ ...req, headers: { ...req.headers, token } });
    setSubmitting(false);

    if (result?.data?.success) {
      onSuccess?.(mode === 'edit' ? 'Invoice updated successfully.' : 'Invoice added successfully.');
      onHide();
    } else {
      setError(result?.data?.message || 'Operation failed. Please try again.');
    }
  };

  /* ---------- render helpers ---------- */
  const field = (label, required, children) => (
    <div className="ms-dialog__field">
      <label className="ms-dialog__label">
        {label}
        {required && <span className="ms-dialog__required"> *</span>}
      </label>
      {children}
    </div>
  );

  const renderSelectField = (value, options, onChange, config = {}) => {
    const {
      labelKey = 'label',
      valueKey = 'id',
      disabled = false,
      className = '',
    } = config;
    return (
      <div className="ms-dialog__select-wrap">
        <FormControl fullWidth size="small">
          <MuiSelect
            displayEmpty
            value={value}
            onChange={onChange}
            disabled={disabled}
            input={(
              <OutlinedInput
                className={`ms-dialog__input ms-dialog__input--mui ${className}`.trim()}
              />
            )}
            renderValue={(selectedValue) => renderSelectValue(selectedValue, options, labelKey, valueKey)}
            IconComponent={() => null}
            MenuProps={{
              disablePortal: false,
              sx: {
                zIndex: 9105,
              },
              MenuListProps: {
                sx: {
                  maxHeight: 181,
                  overflowY: 'auto',
                  paddingTop: 0,
                  paddingBottom: 0,
                },
              },
              PaperProps: {
                className: 'ms-dialog__menu-paper',
                sx: {
                  overflow: 'hidden',
                },
              },
            }}
          >
            <MenuItem value="">
              <span className="ms-dialog__placeholder">Select</span>
            </MenuItem>
            {options.map((item) => (
              <MenuItem key={item[valueKey]} value={String(item[valueKey])}>
                {item[labelKey]}
              </MenuItem>
            ))}
          </MuiSelect>
        </FormControl>
        <img src={dropdwn} alt="dropdown" className="ms-dialog__select-icon" />
      </div>
    );
  };

  const renderDateField = (value, keyName, placeholder = 'dd-mm-yyyy') => (
    <div className="ms-dialog__date-picker">
      <Calendar
        value={value ? new Date(value) : null}
        onChange={(val) => {
          const newValue = val.value;
          if (!newValue) {
            setField(keyName, '');
            return;
          }
          setField(keyName, dayjs(newValue).format('YYYY-MM-DD'));
        }}
        dateFormat="dd-mm-yy"
        placeholder={placeholder}
        disabled={submitting || loading}
        inputClassName="ms-dialog__input"
        style={{ width: '100%' }}
      />
    </div>
  );

  const isCreditNote = invoiceTypes.some(
    (invType) => String(invType.id) === form.invoiceTypeId && invType.label.toLowerCase() === 'credit note',
  );

  return (
    <div className="ms-dialog-overlay" role="dialog" aria-modal="true">
      <div className="ms-dialog ms-dialog--xl ms-dialog--invoice-form">
        <div className="ms-dialog__header">
          {mode === 'edit' ? 'Edit Invoice' : 'Add Invoice'}
        </div>

        {loading ? (
          <div className="ms-dialog__loading">Loading...</div>
        ) : (
          <div className="ms-dialog__form ms-dialog__form--4col ms-dialog__form--invoice">
            {/* Row 1: PO Number | Milestone Id | Milestone Status | Invoice Status */}
            {field('PO Number', true,
              <div style={{ position: 'relative' }} ref={poWrapRef}>
                <input
                  type="text"
                  className="ms-dialog__input"
                  placeholder="PO Number"
                  autoComplete="off"
                  value={form.poText}
                  onChange={(ev) => {
                    const text = ev.target.value;
                    setField('poText', text);
                    setField('poId', '');
                    clearTimeout(poSearchTimerRef.current);

                    if (text.length >= 2) {
                      poSearchTimerRef.current = setTimeout(async () => {
                        const res = await UseAxios(searchPoNumbers(text));
                        const results = res?.data?.data || [];
                        setFilteredPoNumbers(results);

                        // Check for exact match (case-insensitive) on every keystroke
                        const exactMatch = results.find(
                          (po) => po.po_number.toLowerCase() === text.toLowerCase(),
                        );

                        if (exactMatch) {
                          setField('poId', String(exactMatch.po_id));
                          setField('poText', exactMatch.po_number);
                          if (exactMatch.credit_period != null) {
                            setField('collectionCreditPeriod', String(exactMatch.credit_period));
                          }
                          setShowPoSuggestions(false);
                        } else {
                          setShowPoSuggestions(results.length > 0);
                        }
                      }, 300);
                    } else {
                      setShowPoSuggestions(false);
                      setFilteredPoNumbers([]);
                    }
                  }}
                  onBlur={() => {
                    const exactMatch = filteredPoNumbers.find(
                      (po) => po.po_number.toLowerCase() === form.poText.toLowerCase(),
                    );

                    if (exactMatch) {
                      setField('poText', exactMatch.po_number);
                      setField('poId', String(exactMatch.po_id));
                      if (exactMatch.credit_period != null) {
                        setField('collectionCreditPeriod', String(exactMatch.credit_period));
                      }
                    }

                    setTimeout(() => setShowPoSuggestions(false), 200);
                  }}
                />
                {showPoSuggestions && (
                  <div className="ms-po-suggestions">
                    {filteredPoNumbers.map((po) => (
                      <div
                        key={po.po_id}
                        className="ms-po-suggestion-item"
                        onMouseDown={(ev) => {
                          ev.preventDefault();
                          setField('poText', po.po_number);
                          setField('poId', String(po.po_id));
                          if (po.credit_period != null) {
                            setField('collectionCreditPeriod', String(po.credit_period));
                          }
                          setShowPoSuggestions(false);
                          setFilteredPoNumbers([]);
                        }}
                      >
                        {po.po_number}
                      </div>
                    ))}
                  </div>
                )}
              </div>,
            )}

            {field('Milestone Id', false,
              <input
                type="text"
                className="ms-dialog__input ms-dialog__input--readonly"
                value={form.milestoneId}
                readOnly
              />,
            )}

            {field('Milestone Status', true, renderSelectField(
              form.milestoneStatusId,
              msStatuses,
              (ev) => {
                setField('milestoneStatusId', ev.target.value);
              },
            ))}

            {field('Milestone Type', true, renderSelectField(
              form.milestoneTypeId,
              msTypes,
              (ev) => {
                setField('milestoneTypeId', ev.target.value);
              },
            ))}

            {field('Invoice Status', true, renderSelectField(
              form.invoiceStatusId,
              invStatuses,
              (ev) => {
                setField('invoiceStatusId', ev.target.value);
              },
            ))}

            {/* Row 2 */}
            {field('Milestone', true,
              <input
                type="text"
                className="ms-dialog__input"
                placeholder="Enter Milestone"
                value={form.milestone}
                onChange={(ev) => {
                  setField('milestone', ev.target.value);
                }}
              />,
            )}

            {field('Category', true,
              <input
                type="text"
                className="ms-dialog__input"
                placeholder="Enter..."
                value={form.category}
                onChange={(ev) => {
                  setField('category', ev.target.value);
                }}
              />,
            )}

            {field('Zoho Client Name', false,
              <input
                type="text"
                className="ms-dialog__input ms-dialog__input--readonly"
                value={zohoClientName}
                readOnly
              />,
            )}

            {field('% (Of Contract Value)', true,
              <input
                type="number"
                className="ms-dialog__input"
                placeholder="Enter %"
                value={form.contValueP}
                onChange={(ev) => {
                  setField('contValueP', ev.target.value);
                }}
              />,
            )}

            {field('Due Amount (INR)', true,
              <input
                type="number"
                className="ms-dialog__input"
                placeholder="Enter Due Amount (INR)"
                value={form.amount}
                onChange={(ev) => {
                  setField('amount', ev.target.value);
                }}
              />,
            )}

            {field('COGS (INR)', false,
              <input
                type="number"
                className="ms-dialog__input"
                placeholder="Enter COGS (INR)"
                value={form.cogs}
                onChange={(ev) => {
                  setField('cogs', ev.target.value);
                }}
              />,
            )}

            {field('GA (INR)', false,
              <input
                type="number"
                className="ms-dialog__input"
                placeholder="Enter GA (INR)"
                value={form.ga}
                onChange={(ev) => {
                  setField('ga', ev.target.value);
                }}
              />,
            )}

            {field('TP (INR)', false,
              <input
                type="number"
                className="ms-dialog__input"
                placeholder="Enter TP (INR)"
                value={form.tp}
                onChange={(ev) => {
                  setField('tp', ev.target.value);
                }}
              />,
            )}

            {/* Row 3 */}
            {field('Due Date', true, renderDateField(form.dueDate, 'dueDate'))}
            {field('Replanned Due Date', true, renderDateField(form.replannedDueDate, 'replannedDueDate'))}

            {field('Milestone Remarks', false,
              <input
                type="text"
                className="ms-dialog__input"
                placeholder="Enter..."
                value={form.milestoneRemarks}
                onChange={(ev) => {
                  setField('milestoneRemarks', ev.target.value);
                }}
              />,
            )}

            <div className="ms-dialog__section-divider" aria-hidden="true" />

            {field('Zoho Invoice Number', true,
              <div style={{ position: 'relative' }} ref={zohoWrapRef}>
                <input
                  type="text"
                  className="ms-dialog__input"
                  placeholder="Zoho Invoice Number"
                  autoComplete="off"
                  value={zohoText}
                  onChange={(ev) => {
                    const text = ev.target.value;
                    setZohoText(text);

                    const exactMatch = zohoInvoices.find(
                      (inv) => inv.zoho_invoice_number.toLowerCase() === text.toLowerCase(),
                    );

                    if (exactMatch) {
                      setField('zohoInvoiceId', String(exactMatch.zoho_invoice_id));

                      const zDate = exactMatch.invoice_date || exactMatch.date || exactMatch.zoho_invoice_date;
                      if (zDate) {
                        setField('invoiceDate', parseDateForInput(zDate));
                      }

                      setShowZohoSuggestions(false);
                      setFilteredZohoInvoices([]);
                    } else {
                      setField('zohoInvoiceId', '');

                      if (text.length >= 2) {
                        const filtered = zohoInvoices.filter((zohoInv) =>
                          zohoInv.zoho_invoice_number.toLowerCase().includes(text.toLowerCase()),
                        );
                        setFilteredZohoInvoices(filtered);
                        setShowZohoSuggestions(filtered.length > 0);
                      } else {
                        setShowZohoSuggestions(false);
                        setFilteredZohoInvoices([]);
                      }
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => setShowZohoSuggestions(false), 200);
                  }}
                />
                {showZohoSuggestions && (
                  <div className="ms-po-suggestions">
                    {filteredZohoInvoices.map((zohoInv) => (
                      <div
                        key={zohoInv.zoho_invoice_id}
                        className="ms-po-suggestion-item"
                        onMouseDown={(ev) => {
                          ev.preventDefault();
                          setZohoText(zohoInv.zoho_invoice_number);
                          setField('zohoInvoiceId', String(zohoInv.zoho_invoice_id));

                          const zDate = zohoInv.invoice_date || zohoInv.date || zohoInv.zoho_invoice_date;
                          if (zDate) {
                            setField('invoiceDate', parseDateForInput(zDate));
                          }

                          setShowZohoSuggestions(false);
                          setFilteredZohoInvoices([]);
                        }}
                      >
                        {zohoInv.zoho_invoice_number}
                      </div>
                    ))}
                  </div>
                )}
              </div>,
            )}

            {/* Row 4 */}
            {mode === 'edit' && field('Invoice ID', false,
              <input
                type="text"
                className="ms-dialog__input ms-dialog__input--readonly"
                value={form.invoiceId}
                readOnly
              />,
            )}

            {field('Invoice Amount (INR)', true,
              <input
                type="number"
                className="ms-dialog__input"
                placeholder="Enter Invoice Amount (INR)"
                value={form.invoiceAmount}
                onChange={(ev) => {
                  setField('invoiceAmount', ev.target.value);
                }}
              />,
            )}

            {field('Invoice COGS (INR)', false,
              <input
                type="number"
                className="ms-dialog__input"
                placeholder="Enter Invoice COGS (INR)"
                value={form.invoiceCogs}
                onChange={(ev) => {
                  setField('invoiceCogs', ev.target.value);
                }}
              />,
            )}

            {field('Invoice GA (INR)', false,
              <input
                type="number"
                className="ms-dialog__input"
                placeholder="Enter Invoice GA (INR)"
                value={form.invoiceGa}
                onChange={(ev) => {
                  setField('invoiceGa', ev.target.value);
                }}
              />,
            )}

            {field('Invoice TP (INR)', false,
              <input
                type="number"
                className="ms-dialog__input"
                placeholder="Enter Invoice TP (INR)"
                value={form.invoiceTp}
                onChange={(ev) => {
                  setField('invoiceTp', ev.target.value);
                }}
              />,
            )}

            <div className="ms-dialog__field ms-dialog__field--tax-wrap" ref={taxWrapRef}>
              <label className="ms-dialog__label">
                Invoice Amount with Tax (INR) <span className="ms-dialog__required">*</span>
              </label>
              <div className="ms-tax-input-row">
                <input
                  type="number"
                  className="ms-dialog__input"
                  placeholder="Enter Invoice Amount with Tax (INR)"
                  value={form.invoiceAmountWithTax}
                  onChange={(ev) => {
                    setField('invoiceAmountWithTax', ev.target.value);
                  }}
                />
              </div>
              <div className="ms-tax-trigger-row">
                <button
                  type="button"
                  className="ms-tax-btn"
                  onClick={() => {
                    setShowTaxPopover((prev) => !prev);
                  }}
                >
                  Tax%
                </button>
              </div>
              {showTaxPopover && (
                <div className="ms-tax-popover">
                  <div className="ms-tax-popover__title-row">
                    <div className="ms-tax-popover__title">Add Tax %</div>
                    <div className="ms-tax-popover__info-wrap">
                      <button
                        type="button"
                        className="ms-tax-popover__icon-btn"
                        aria-label="Tax formula"
                      >
                        <span className="ms-tax-popover__icon">i</span>
                      </button>
                      <div className="ms-tax-popover__formula-bubble">
                        Invoice amount with tax = Invoice amount + ((Tax % / 100) * Invoice amount)
                      </div>
                    </div>
                  </div>
                  <input
                    type="number"
                    className="ms-dialog__input"
                    placeholder="Enter tax %"
                    value={taxPercent}
                    onChange={(ev) => {
                      setTaxPercent(ev.target.value);
                    }}
                  />
                  <div className="ms-tax-popover__actions">
                    <button
                      type="button"
                      className="ms-btn ms-btn--ghost"
                      onClick={() => {
                        setShowTaxPopover(false);
                        setTaxPercent('');
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="ms-btn ms-btn--primary"
                      onClick={applyTax}
                    >
                      Add
                    </button>
                  </div>
                </div>
              )}
            </div>

            {field('Invoice Date', true, renderDateField(form.invoiceDate, 'invoiceDate'))}

            {field('Invoice Type', true, renderSelectField(
              form.invoiceTypeId,
              invoiceTypes,
              (ev) => {
                const newTypeId = ev.target.value;
                setField('invoiceTypeId', newTypeId);
                const selectedType = invoiceTypes.find((invType) => String(invType.id) === newTypeId);
                if (!selectedType || selectedType.label.toLowerCase() !== 'credit note') {
                  setField('creditNoteLinkedId', '');
                }
              },
            ))}

            {/* Row 5 */}
            {field('Invoice Id Related to Credit Note', isCreditNote, renderSelectField(
              form.creditNoteLinkedId,
              projectInvoices.map((inv) => ({
                ...inv,
                display_label: inv.zoho_invoice_number
                  ? `${inv.invoice_id} - ${inv.zoho_invoice_number}`
                  : String(inv.invoice_id),
              })),
              (ev) => {
                setField('creditNoteLinkedId', ev.target.value);
              },
              {
                labelKey: 'display_label',
                valueKey: 'invoice_id',
                disabled: !isCreditNote,
              },
            ))}

            {field('Revenue Recognition Date', true, renderDateField(form.revenueDate, 'revenueDate'))}

            {field('Invoice Remarks', false,
              <input
                type="text"
                className="ms-dialog__input"
                placeholder="Enter..."
                value={form.remarks}
                onChange={(ev) => {
                  setField('remarks', ev.target.value);
                }}
              />,
            )}

            {field('Billed Effort (hrs)', false,
              <input
                type="number"
                className="ms-dialog__input"
                placeholder="Enter Billed Effort (hrs)"
                value={form.billedEffort}
                onChange={(ev) => {
                  setField('billedEffort', ev.target.value);
                }}
              />,
            )}

            {/* Row 6 */}
            {field('Payment Status', true, renderSelectField(
              form.paymentStatusId,
              paymentStatuses,
              (ev) => {
                setField('paymentStatusId', ev.target.value);
              },
            ))}

            {field('Collection Credit Period (Days)', true,
              <input
                type="number"
                className="ms-dialog__input"
                placeholder="Enter Collection Credit Period (Days)"
                value={form.collectionCreditPeriod}
                onChange={(ev) => {
                  setField('collectionCreditPeriod', ev.target.value);
                }}
              />,
            )}

          </div>
        )}

        {error && <div className="ms-dialog__error">{error}</div>}

        <div className="ms-dialog__footer">
          <button
            type="button"
            className="ms-btn ms-btn--ghost"
            onClick={onHide}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="ms-btn ms-btn--primary"
            onClick={handleSubmit}
            disabled={submitting || loading}
          >
            {submitting
              ? (mode === 'edit' ? 'Updating...' : 'Adding...')
              : (mode === 'edit' ? 'Update' : 'Add')}
          </button>
        </div>
      </div>
    </div>
  );
};

InvoiceFormDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['add', 'edit']).isRequired,
  milestoneRow: PropTypes.object,
  rowData: PropTypes.shape({
    invoice_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    invoiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  token: PropTypes.string,
  onSuccess: PropTypes.func,
};

InvoiceFormDialog.defaultProps = {
  milestoneRow: null,
  rowData: null,
  token: '',
  onSuccess: null,
};

export default InvoiceFormDialog;
